import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { User } from "../models/auth.models";
import { environment } from "../../../environments/environment";
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap } from "rxjs";
import { UtilsService } from "./utils.service";
import { ICurrentUser } from "../models/current-user";

@Injectable({ providedIn: "root" })
export class AuthenticationService {
  private refresh: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  isRefreshing = false;

  private isAuthKnown = false;

  private user: ICurrentUser;
  private authUrl = `${environment.url}/auth`;
    private userUrl = `${environment.url}/v1/users`;

  //private readonly ACCESS_TOKEN = "accessToken";

  private meRequest$: Observable<any>;

  constructor(private http: HttpClient, private utilsService: UtilsService) { }

  get MEURH360_Admin() {
    return "Administrador MEURH360";
  }

  public getCurrentUserUpdates(): Observable<boolean> {
    return this.refresh.asObservable();
  }

  public setToRefresh(value: boolean): void {
    this.refresh.next(value);
  }

  public saveCurrentUser(user: any) {
    this.user = {
      payload: {
        ...user,
        name: user?.userName,
        profile: user?.userProfile?.name,
        role: user?.userProfile?.roles,
      },
    };

    this.isAuthKnown = true;
  }

  public get isFirstAccess(): boolean {
    const currentUser = this.currentUser()?.payload;
    return currentUser?.firstAccess;
  }

  public get userFranchises(): number[] {
    const user = this.currentUser()?.payload;
    return user?.branches || [];
  }

  public get canSendProfileToHR(): boolean {
    const user = this.currentUser()?.payload;
    return user?.employeeDataStatus != 2 && user?.employeeDataStatus != 3;
  }

  public get isPendingProfile(): boolean {
    const user = this.currentUser()?.payload;
    return user?.employeeDataStatus == 1;
  }

  public get nickname() {
    const user = this.currentUser()?.payload;
    return this.utilsService.getUserNickname(user.name);
  }

  public get username() {
    const user = this.currentUser()?.payload;
    return user.name;
  }
  public get employeeRoleId() {
    const user = this.currentUser()?.payload;
    return user.roleId;
  }

  public get Subordinates() {
    const user = this.currentUser()?.payload;
    return user.subordinatesId;
  }

  public userCanManage(branches: number[]): boolean {
    if (!this.userFranchises.length) return true;
    return branches.every((id) => this.userFranchises.includes(id));
  }

  public update() {
    return this.http
      .put(this.authUrl, {})
      .pipe(tap(() => this.setToRefresh(true)));
  }
  /**
   * Returns the current user
   */
  public currentUser(): ICurrentUser {
    return this.user;
  }

  public employeeId() {
    return this.currentUser()?.payload?.employeeId;
  }

  public get isNewUSerCompanyWasNotConfigured(): boolean {
    const user = this.currentUser();
    return (
      user?.payload?.profile !== this.MEURH360_Admin &&
      (user?.payload?.companyId == 0 || !user?.payload?.companyId)
    );
  }

  public get IsHiringProcess(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "COLABORADOR_EM_ADMSSAO";
  }

  public get IsDefaultUser(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Usuario Colaborador";
  }

  public get IsHRUser(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Administrador Cliente";
  }


  public get IsPerformanceAdmin(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Gestão de desempenho administrativo";
  }

  public get isTeamLeader(): boolean {
    const user = this.currentUser();
    return user?.payload?.isTeamLead;
  }

  public get IsHRBP(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "HRBP";
  }

  public get IsAttendant(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Atendimento";
  }

  public get IsConsult(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Consultoria";
  }

  public get canManageEmployeeProfile(): boolean {
    const user = this.currentUser();
    return user?.payload?.role?.includes("ADMNISTRAR_ON_OFF_BOARD");
  }

  public get canAttendantIsShowList(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Atendimento" && user?.payload?.role?.includes("LISTAR_ATENDIMENTO");
  }

  public get canAttendantApprove(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Atendimento" && user?.payload?.role?.includes("APROVAR_ATENDIMENTO");
  }

  public get canAttendantRepprove(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Atendimento" && user?.payload?.role?.includes("REPROVAR_ATENDIMENTO");
  }

  public get canAttendantShow(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Atendimento" && user?.payload?.role?.includes("CONSULTAR_ATENDIMENTO");
  }

  public get canAttendantReport(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Atendimento" && user?.payload?.role?.includes("CONSULTAR_RELATORIOS");
  }

  public get canConsultShowEmployee(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Consultoria" && user?.payload?.role?.includes("GESTAO_COLABORADOR_VIEW");
  }

  public get canConsultShowAttendant(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Consultoria" && user?.payload?.role?.includes("ATENDIMENTOS_VIEW");
  }

  public get canConsultShowReport(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Consultoria" && user?.payload?.role?.includes("BI360_VIEW");
  }

  public get IsReadOnly(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === "Consultoria" || user?.payload?.readOnly?.toUpperCase() === "TRUE";
  }

  public get userRoles(): string[] {
    const user = this.currentUser();
    return user?.payload?.role;
  }

  public get userId(): number {
    const user = this.currentUser();
    return +user?.payload?.id;
  }

  public get profile(): string {
    const user = this.currentUser();
    return user?.payload?.profile;
  }
  public get IsManager(): boolean {
    const user = this.currentUser();
    return user?.payload?.isManager;
  }

  public get employeeSubordinates(): number[] {
    const user = this.currentUser();
    return user?.payload?.subordinatesId;
  }

  isAdmin(): boolean {
    const user = this.currentUser();
    return user?.payload?.profile === this.MEURH360_Admin;
  }

  public get registerWorkedHours(): boolean {
    const user = this.currentUser();
    return user?.payload?.registerWorkedHours;
  }

  get getCurrentUser() {
    return this.currentUser()?.payload;
  }

  get getCompanyName() {
    return this.currentUser()?.payload?.companyName?.trim();
  }
  /**
   * Performs the auth
   * @param email email of user
   * @param password password of user
   */
  // public login(email: string, password: string) {
  //   return this.http.post(
  //     `${this.authUrl}/login`,
  //     {
  //       email: email,
  //       password: password,
  //     },
  //     { withCredentials: true }
  //   );
  // }

  public login(email: string, password: string) {

    return this.http.post(
      `${this.authUrl}/login`,
      {
        email,
        password,
      },
      { withCredentials: true }
    ).pipe(
      tap(user => this.saveCurrentUser(user))
    );

  }



  /**
   * Performs the register
   * @param email email
   * @param password password
   */
  public register(
    username: string,
    email: string,
    password: string,
    confirmPassword: string,
    agreement: boolean
  ) {
    return this.http.post(`${environment.url}/v1/accounts`, {
      fullName: username,
      email: email,
      password: password,
      confirmPassword: confirmPassword,
      agreement: agreement,
    });
  }

  /**
   * Reset password
   * @param email email
   */
  public resetPassword(email) {
    return this.http.post(`${this.authUrl}/reset`, email);
  }

  public canManagerAccessEmployeeData() {
    return this.http.get(
      `${environment.url}/v1/employees/access-employee-data`
    );
  }

  public canEditEmployeeArea(): Observable<boolean> {
    return this.http.get<boolean>(
      `${environment.url}/v1/employees/edit-area-enabled`
    );
  }

  public changePassword(payload, accessToken: string) {
    return this.http.put(`${this.authUrl}/reset`, payload, {
      headers: { Authorization: `bearer ${accessToken}` },
    });
  }


  logout() {

    this.isAuthKnown = false;

    this.user = null;

    return this.http.post(`${this.authUrl}/logout`, {}, {
      withCredentials: true
    });

    // return this.http.post(`${this.authUrl}/logout`, {}, {
    //   withCredentials: true,
    // }).pipe(
    //   tap(() => this.user = null)
    // );
  }

  ssoLogin = (idp: string) =>
    `${environment.url.replace("api", "sso")}/saml/redirect?idp=${idp}`;

  getRefreshToken() {
    return this.http.post(`${this.authUrl}/refresh`, {});
  }

  // getMe() {
  //   return this.http.get(`${this.authUrl}/me`, {
  //     withCredentials: true,
  //   });
  // }


  getMe(): Observable<any> {

    if (this.user)
      return of(this.user.payload);

    if (this.meRequest$)
      return this.meRequest$;

    this.meRequest$ = this.http.get(`${this.authUrl}/me`, {
      withCredentials: true,
    }).pipe(

      tap(user => this.saveCurrentUser(user)),

      tap(() => this.meRequest$ = null),

      catchError(err => {
        this.meRequest$ = null;
        throw err;
      })

    );

    return this.meRequest$;

  }

  getIdps() {
    return this.http
      .get(`${this.authUrl}/idps`)
      .pipe(tap((res) => console.log(res)));
  }

  checkAuth(): Observable<boolean> {

    if (this.user)
      return of(true);

    return this.http.get<any>(
      `${this.authUrl}/session`,
      { withCredentials: true }
    ).pipe(

      map(r => r.authenticated),

      catchError(() => of(false))

    );

  }

  /*checkAuth(): Observable<boolean> {

  // já autenticado em memória
  if (this.user)
    return of(true);

  // tenta renovar sessão usando refreshToken cookie
  return this.getRefreshToken().pipe(

    map(() => true),

    catchError(() => of(false))

  );

}*/

  isAuthenticated(): Observable<boolean> {

    // já autenticado nesta sessão
    if (this.user)
      return of(true);

    // já sabemos que NÃO está autenticado
    if (this.isAuthKnown)
      return of(false);

    // ainda não sabemos → tenta validar uma única vez
    return this.getMe().pipe(

      tap(user => {
        this.saveCurrentUser(user);
      }),

      map(() => true),

      catchError(() => {

        this.isAuthKnown = true;

        return of(false);

      })

    );

  }


  // hasToken(): boolean {

  //   const token = this.cookieService.get(this.ACCESS_TOKEN);

  //   if (!token)
  //     return false;

  //   if (token === "null" || token === "undefined")
  //     return false;

  //   return token.length > 10;
  // }
  
  resetPasswordOnFirstAccess(value: any) {
    //const url = `${this.userUrl}/reset-password`;
    const url = `${this.authUrl}/first-access`;

    return this.http.post(url, value);
  }

}
